/**
 * The typed control for one workflow field (step-model E2 TaskForm, lifted
 * for SA-401 so the intake form and the task form render a field the same
 * way): enum → NativeSelect, boolean → Switch, text → Textarea, everything
 * else an Input with the matching HTML type. Booleans render
 * `checked={value === true}`, so an unset value shows off whether the
 * caller seeds it as `false` (TaskForm) or leaves it `null` (the SA-401
 * intake surfaces, finding F2 — an untouched boolean must mean "unknown",
 * not an authored "no"). The control id is `${idPrefix}-${field.id}` so a
 * sibling <label htmlFor> matches exactly.
 *
 * SA-406: a date / datetime / time control is handed `controlValue(...)`,
 * never the raw stored string — a browser silently blanks a value that is
 * not in the control's own shape while the form would still submit it. An
 * offset-bearing datetime shows as wall time in the deployment zone (a
 * naive datetime field value IS deployment-local wall time, D1); what the
 * control still cannot show is printed under it.
 *
 * SA-407: `describedBy` is an optional element id the caller wants the
 * control described by (TaskForm's "Suggested by AI" chip). It lands on
 * whichever control renders, after SA-406's own fallback-line id. The intake
 * surfaces pass nothing and get no attribute.
 *
 * SA-449: a `table` renders `TableFieldInput`, whose cells are this control.
 *
 * SA-1051: a `place` renders `PlaceInput`, the one React Query consumer here.
 */
import { Input, NativeSelect, Switch, Textarea } from '@/components/ui'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { PlaceInput } from './PlaceInput'
import { TableFieldInput } from './TableFieldInput'
import { WindowInput } from './WindowInput'

export interface FieldLike {
  id: string
  type: string
  options?: string[] | null
  /** SA-449: a table's own label (its group name) and its columns. */
  label?: string
  columns?: TableColumnLike[] | null
  /** SA-1052: a place field that may hold the customer's alternatives. */
  alternatives?: boolean | null
}

/** SA-449: a column of a `table` field — a scalar field with a label. */
export type TableColumnLike = FieldLike & { label: string; optional?: boolean; reference?: string | null }

export function coerceFieldValue(field: FieldLike, raw: string): unknown {
  if (raw === '') return null
  if (field.type === 'number') return Number(raw)
  if (field.type === 'integer') return parseInt(raw, 10)
  return raw
}

const HTML_TYPES: Record<string, string> = {
  number: 'number', integer: 'number', date: 'date', datetime: 'datetime-local',
  time: 'time', email: 'email', phone: 'tel',
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?$/
const OFFSET_RE = /(Z|[+-]\d{2}:?\d{2})$/i
const ZONED_DATETIME_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/i
const UNDISPLAYABLE_WORDS: Record<string, string> = { date: 'date', datetime: 'date and time', time: 'time' }

// Shape is not enough: a browser also blanks `2026-02-30` and `24:00`, so
// "displayable" means a real calendar date (year 1+) and a real clock time.
function isDate(text: string): boolean {
  const m = DATE_RE.exec(text)
  if (!m) return false
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])]
  if (year < 1) return false
  const probe = new Date(0)
  probe.setUTCFullYear(year, month - 1, day) // Date.UTC would read years 0–99 as 19xx
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day
}

function isTime(text: string): boolean {
  const m = TIME_RE.exec(text)
  return !!m && Number(m[1]) < 24 && Number(m[2]) < 60 && (m[3] === undefined || Number(m[3]) < 60)
}

function isLocalDatetime(text: string): boolean {
  const parts = text.split('T')
  return parts.length === 2 && isDate(parts[0]) && isTime(parts[1])
}

function wallTime(instant: Date, timeZone: string): string {
  const partsIn = (zone: string) => new Intl.DateTimeFormat('en-CA', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(instant)
  let parts: Intl.DateTimeFormatPart[]
  try { parts = partsIn(timeZone) } catch { parts = partsIn('UTC') }
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00'
  const base = `${get('year').padStart(4, '0')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
  return get('second') === '00' ? base : `${base}:${get('second')}`
}

// Any zone is within a day of UTC, so from here on the wall-clock year is 1+
// everywhere (Intl prints 1 BC as year "1", which would read as 1 AD).
const EARLIEST_INSTANT = new Date(0).setUTCFullYear(1, 0, 2)

/** An offset-bearing ISO datetime as wall time in `timeZone`, or null. The
 * gate is strict and the string is canonicalized before `Date.parse`, whose
 * non-ISO fallback is engine-specific and lenient ("next Tuesday +0700"). */
function zonedToWallTime(text: string, timeZone: string): string | null {
  const m = ZONED_DATETIME_RE.exec(text)
  if (!m || !isDate(m[1]) || !isTime(m[2])) return null
  const clock = m[2].length === 5 ? `${m[2]}:00` : m[2]
  const fraction = m[3] ? m[3].slice(0, 4) : ''
  const offset = m[4].toUpperCase() === 'Z' ? 'Z' : m[4].replace(/^([+-]\d{2}):?(\d{2})$/, '$1:$2')
  const ms = Date.parse(`${m[1]}T${clock}${fraction}${offset}`)
  if (Number.isNaN(ms) || ms < EARLIEST_INSTANT) return null
  const wall = wallTime(new Date(ms), timeZone)
  return isLocalDatetime(wall) ? wall : null // e.g. year 10000 east of UTC
}

/** What the HTML control can show for a stored value: `''` for empty,
 * `null` when a non-empty value cannot be shown at all (SA-406). */
export function controlValue(type: string, value: unknown, timeZone: string): string | null {
  const str = value === null || value === undefined ? '' : String(value)
  if (str === '' || !(type in UNDISPLAYABLE_WORDS)) return str
  if (type === 'date') return isDate(str) ? str : null
  if (type === 'time') {
    const bare = str.replace(OFFSET_RE, '')
    return isTime(bare) ? bare : null
  }
  return isLocalDatetime(str) ? str : zonedToWallTime(str, timeZone)
}

export function FieldInput({ field, value, onChange, idPrefix = 'f', describedBy }: {
  field: FieldLike
  value: unknown
  onChange: (v: unknown) => void
  idPrefix?: string
  describedBy?: string
}) {
  const id = `${idPrefix}-${field.id}`
  const timeZone = useDefaultTimezone()
  if (field.type === 'table') {
    return <TableFieldInput field={field} value={value} onChange={onChange} idPrefix={idPrefix} describedBy={describedBy} Cell={FieldInput} />
  }
  if (field.type === 'place') {
    // SA-1051 (spec §8): the one picker; a child, so this control stays provider-free for every other type.
    return <PlaceInput id={id} value={value} onChange={onChange} describedBy={describedBy} allowAlternatives={field.alternatives === true} />
  }
  if (field.type === 'window') {
    // SA-1136 (spec §6.3): the one editor; provider-free, so the Orders lane's page rule holds.
    return <WindowInput id={id} value={value} onChange={onChange} describedBy={describedBy} />
  }
  const str = value === null || value === undefined ? '' : String(value)
  if (field.type === 'enum') {
    return (
      <NativeSelect id={id} value={str} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">—</option>
        {(field.options ?? []).map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </NativeSelect>
    )
  }
  if (field.type === 'boolean') {
    return <Switch id={id} checked={value === true} aria-describedby={describedBy} onCheckedChange={(c) => onChange(c)} />
  }
  if (field.type === 'text') {
    return <Textarea id={id} value={str} aria-describedby={describedBy} onChange={(e) => onChange(coerceFieldValue(field, e.target.value))} rows={3} />
  }
  const shown = controlValue(field.type, value, timeZone)
  return (
    <>
      <Input
        id={id} type={HTML_TYPES[field.type] ?? 'text'} value={shown ?? ''}
        aria-describedby={[shown === null ? `${id}-current` : null, describedBy].filter(Boolean).join(' ') || undefined}
        onChange={(e) => onChange(coerceFieldValue(field, e.target.value))}
      />
      {shown === null && (
        <p id={`${id}-current`} data-testid="undisplayable-value" className="text-xs text-amber-700">
          Current value: <code>{str}</code> — not a valid {UNDISPLAYABLE_WORDS[field.type]}; choose one to replace it.
        </p>
      )}
    </>
  )
}
